/**
 * "Duplica ordine" per un ordine WooCommerce: le righe del negozio diventano
 * righe di un ordine manuale.
 *
 * Prima si metteva l'id WooCommerce (un numero) in `order_items.product_id`,
 * che e' l'uuid del prodotto del magazzino: l'inserimento falliva sempre, e
 * l'IVA era fissa al 22% qualunque fosse quella dell'ordine.
 * Ora ogni riga si aggancia al prodotto del magazzino tramite
 * `products.woocommerce_product_id` (o, in mancanza, lo SKU) e l'IVA si ricava
 * dalla riga WooCommerce (`total_tax` / `total`).
 */
import type { OrderItemInput } from '../types/orders';

/** Riga come sta in `woocommerce_orders.order_data.line_items` */
export interface WooLineItemForDuplicate {
  product_id?: number | null;
  variation_id?: number | null;
  name?: string;
  sku?: string | null;
  quantity?: number;
  total?: string;
  total_tax?: string;
}

/** Prodotto del magazzino con i campi che servono all'aggancio */
export interface WarehouseProductRef {
  id: string;
  name?: string | null;
  sku?: string | null;
  woocommerce_product_id?: number | string | null;
}

export interface WooDuplicateResult {
  items: OrderItemInput[];
  /** Nomi delle righe senza un prodotto corrispondente in magazzino */
  missing: string[];
}

/** Aliquota IVA della riga WooCommerce, arrotondata a 2 decimali. 0 se non ricavabile. */
export function wooLineTaxRate(total: string | undefined, totalTax: string | undefined): number {
  const imponibile = parseFloat(total || '');
  const tassa = parseFloat(totalTax || '');
  if (!(imponibile > 0) || Number.isNaN(tassa) || tassa < 0) return 0;
  return Math.round((tassa / imponibile) * 100 * 100) / 100;
}

/** Id WooCommerce e SKU da cercare tra i prodotti del magazzino */
export function wooLookupKeys(lineItems: WooLineItemForDuplicate[]): { wooIds: number[]; skus: string[] } {
  const wooIds = new Set<number>();
  const skus = new Set<string>();
  for (const li of lineItems) {
    if (li.product_id) wooIds.add(Number(li.product_id));
    if (li.sku && li.sku.trim()) skus.add(li.sku.trim());
  }
  return { wooIds: [...wooIds], skus: [...skus] };
}

export function wooLineItemsToOrderItems(
  lineItems: WooLineItemForDuplicate[],
  products: WarehouseProductRef[],
): WooDuplicateResult {
  const byWooId = new Map<number, WarehouseProductRef>();
  const bySku = new Map<string, WarehouseProductRef>();
  for (const p of products) {
    if (p.woocommerce_product_id !== null && p.woocommerce_product_id !== undefined && p.woocommerce_product_id !== '') {
      byWooId.set(Number(p.woocommerce_product_id), p);
    }
    if (p.sku && p.sku.trim() && !bySku.has(p.sku.trim())) bySku.set(p.sku.trim(), p);
  }

  const items: OrderItemInput[] = [];
  const missing: string[] = [];
  for (const li of lineItems) {
    const product =
      (li.product_id ? byWooId.get(Number(li.product_id)) : undefined) ??
      (li.sku && li.sku.trim() ? bySku.get(li.sku.trim()) : undefined);
    const nome = li.name || li.sku || 'Prodotto';
    if (!product) {
      missing.push(nome);
      continue;
    }
    const quantity = li.quantity && li.quantity > 0 ? li.quantity : 1;
    const totale = parseFloat(li.total || '0');
    items.push({
      product_id: product.id,
      product_name: li.name || product.name || 'Prodotto',
      product_sku: li.sku || product.sku || undefined,
      quantity,
      unit_price: Number.isFinite(totale) ? Math.round((totale / quantity) * 10000) / 10000 : 0,
      discount_amount: 0,
      tax_rate: wooLineTaxRate(li.total, li.total_tax),
    });
  }
  return { items, missing };
}
